import Fuse from "fuse.js";
import type { FuseOptionKey } from "fuse.js";
import { atom } from "jotai";
import { atomEffect } from "jotai-effect";
import { atomFamily } from "jotai-family";

import { formAtom } from "@frontend/lib/form";

import { contactSchema } from "../schemas/contact";
import { addressBookApi } from "../services";
import { toContactInput } from "../services/contactInput";
import type { Contact } from "../types";
import { paneAtom } from "./pane";
import { addressBookAtom } from "./state";

/**
 * Default contact object used for initializing new contacts.
 */
const defaultContact: Contact = {
  id: 0,
  title: "",
  hasPhoto: false,
  avatarUrl: null,
  isOrganization: false,
  relations: [],
  phones: [],
  emails: [],
  urls: [],
  typedAddresses: [],
  categories: [],
};

export const contactsAtom = atom((get) => get(addressBookAtom).contacts);

export const selectedContactAtom = atom(
  (get) => get(addressBookAtom).selectedContact,
);

export const isContactSelectedAtom = atomFamily((id: number) =>
  atom((get) => get(selectedContactAtom)?.id === id),
);

export const selectContactAtom = atom(
  null,
  async (_get, set, contact: Contact) => {
    // On narrow layouts the detail replaces the list
    set(paneAtom, "detail");

    try {
      // Set selected contact immediately for instant UI feedback
      // set(addressBookAtom, (draft) => {
      //   draft.selectedContact = contact;
      // });

      // Reset mode to "view" when a contact is selected, but preserve "select" mode
      set(addressBookAtom, (draft) => {
        if (draft.mode !== "select") {
          draft.mode = "view";
        }
      });

      const fetchedContact = await addressBookApi.fetchContactsShow(
        contact.id.toString(),
      );

      // Update selected contact with fetched details
      set(addressBookAtom, (draft) => {
        draft.selectedContact = {
          ...draft.selectedContact,
          ...fetchedContact,
        };
      });
    } catch (e) {
      console.error("Failed to load contacts:", e);
    }
  },
);

export const newContactAtom = atom(null, (_get, set) => {
  set(paneAtom, "detail");

  set(addressBookAtom, (draft) => {
    draft.selectedContact = { ...defaultContact };
    draft.mode = "edit";
  });
});

export const pendingScrollToContactIdAtom = atom<number | null>(null);

export const pendingPhotoAtom = atom<File | null>(null);

export const isSavingContactAtom = atom(false);

export const createContactAtom = atom(null, async (get, set, data: Contact) => {
  const { addressBookName, selectedCategory } = get(addressBookAtom);
  const pendingPhoto = get(pendingPhotoAtom);

  try {
    set(isSavingContactAtom, true);

    const newContact = await addressBookApi.fetchContactsCreate(
      addressBookName,
      toContactInput(data),
    );

    if (pendingPhoto) {
      await addressBookApi.fetchContactsUpdatePhoto(
        newContact.id.toString(),
        pendingPhoto,
      );
      set(pendingPhotoAtom, null);
    }

    // Refresh the contact list from the API to get the up-to-date sorted list
    if (selectedCategory) {
      const contacts = await addressBookApi.fetchContacts(
        addressBookName,
        selectedCategory.type,
        selectedCategory.key,
      );

      set(addressBookAtom, (draft) => {
        draft.contacts = contacts;
        draft.selectedContact = newContact;
        draft.mode = "view";
      });
    } else {
      set(addressBookAtom, (draft) => {
        draft.contacts.unshift(newContact);
        draft.selectedContact = newContact;
        draft.mode = "view";
      });
    }

    // Signal the list to scroll to the new contact once rendered
    set(pendingScrollToContactIdAtom, newContact.id);
  } catch (e) {
    console.error("Failed to create contact:", e);
  } finally {
    set(isSavingContactAtom, false);
  }
});

export const updateContactAtom = atom(null, async (get, set, data: Contact) => {
  const selectedContact = get(selectedContactAtom);
  const pendingPhoto = get(pendingPhotoAtom);

  if (!selectedContact) {
    console.warn("No contact selected for update");
    return;
  }

  try {
    set(isSavingContactAtom, true);

    const updatedContact = await addressBookApi.fetchContactsUpdate(
      selectedContact.id.toString(),
      toContactInput(data),
    );

    if (pendingPhoto) {
      const photo = await addressBookApi.fetchContactsUpdatePhoto(
        selectedContact.id.toString(),
        pendingPhoto,
      );

      set(pendingPhotoAtom, null);

      // Update avatar url with new timestamp
      updatedContact.avatarUrl = photo.url;
    }

    // Update selected contact and the matching entry in the contacts list
    set(addressBookAtom, (draft) => {
      if (draft.selectedContact) {
        Object.assign(draft.selectedContact, updatedContact);
      }

      const index = draft.contacts.findIndex(
        (c) => c.id === selectedContact.id,
      );

      if (index !== -1) {
        Object.assign(draft.contacts[index], updatedContact);
      }
    });
  } catch (e) {
    console.error("Failed to update contact:", e);
  } finally {
    set(isSavingContactAtom, false);
  }
});

/**
 * Search contact atoms.
 */

export const searchQueryAtom = atom("");

export const SEARCH_FIELDS = ["all", "name", "email", "phone"] as const;

export type SearchField = (typeof SEARCH_FIELDS)[number];

/**
 * Which field the query is matched against. The names are the ones the list
 * endpoint returns, the contact detail is not loaded at this point.
 */
const searchKeys: Record<SearchField, FuseOptionKey<Contact>[]> = {
  all: [
    { name: "displayName", weight: 2 },
    { name: "preferredEmailAddress", weight: 1 },
    { name: "preferredPhoneNumber", weight: 1 },
  ],
  name: [{ name: "displayName", weight: 1 }],
  email: [{ name: "preferredEmailAddress", weight: 1 }],
  phone: [{ name: "preferredPhoneNumber", weight: 1 }],
};

export const searchFieldAtom = atom<SearchField>("all");

const fuseAtom = atom((get) => {
  const contacts = get(contactsAtom);

  return new Fuse(contacts, {
    keys: searchKeys[get(searchFieldAtom)],
    threshold: 0.2,
    ignoreLocation: true,
    minMatchCharLength: 2,
    useExtendedSearch: true,
  });
});

export const filteredContactsAtom = atom((get) => {
  const query = get(searchQueryAtom).trim();

  // If query is empty, return all contacts without using Fuse.js
  if (!query) {
    return get(contactsAtom);
  }

  const fuse = get(fuseAtom);

  return fuse.search(query).map((result) => result.item);
});

/**
 * Sticky header state and atoms.
 */

type StickyState = {
  letter: string | null;
  categoryKey: string | null;
  shouldAnimate?: boolean;
};

const stickyStateAtom = atom<StickyState>({
  letter: null,
  categoryKey: null,
  shouldAnimate: false,
});

export const updateStickyLetterAtom = atom(
  null,
  (get, set, { letter, categoryKey }: StickyState) => {
    const prev = get(stickyStateAtom);

    const categoryChanged = prev.categoryKey !== categoryKey;

    const shouldAnimate =
      !categoryChanged && prev.letter !== null && prev.letter !== letter;

    set(stickyStateAtom, {
      letter: categoryChanged ? null : letter,
      categoryKey: categoryKey,
      shouldAnimate,
    });
  },
);

// Write-only atom to update the sticky header letter.
export const shouldAnimateStickyAtom = atom(
  (get) => get(stickyStateAtom).shouldAnimate,
);

/**
 * Scroll-to-letter atoms.
 *
 * Atom holding the scroll-to-letter implementation registered by the
 * list component. Allows the virtualizer (in ContactList) to handle
 * scrolling without DOM queries.
 */

export const scrollToLetterFnAtom = atom<((letter: string) => void) | null>(
  null,
);

export const scrollToLetterAtom = atom(null, (get, _set, letter: string) => {
  get(scrollToLetterFnAtom)?.(letter);
});

/**
 * Contact form atoms.
 */

// Contact form atom
export const contactForm = formAtom(contactSchema, defaultContact);

// Sync effect to update contact form when selected contact changes
export const contactFormSyncEffect = atomEffect((get, set) => {
  const selected = get(selectedContactAtom);

  set(contactForm.actions.setInitial, selected ?? defaultContact);
});
